// @ts-nocheck
/* eslint-disable */
import React, { useState } from 'react';
import AddEntity from '../../components/CRUD/AddEntity';
import { useTypedSelector } from '../../../helpers';
import { Modal } from 'antd';
import { Checkbox, FormControlLabel } from '@mui/material';
import DocumentTypeTransferList from '../document-types/DocumentTypeTransferList';
import { ApplicableFaresPanel, passesFareGuard } from '../shared/applicableFares';
import { useIntl } from 'react-intl';

import { useLocation } from 'react-router-dom';

const AddDiscountBand = () => {
  const { pathname } = useLocation();
  const isPromotionsRoute = pathname.includes('/fare-products/promotions');
  const productType = isPromotionsRoute ? 'Promotions' : 'FareProducts';
  const pageTitle = isPromotionsRoute ? 'AddNewPromotion' : 'AddNewFareProduct';
  const backUrl = isPromotionsRoute ? '/fare-products/promotions' : '/fare-products/fare-products';
  const productTerm = isPromotionsRoute ? 'Promotion' : 'Fare Product';
  const messageTransformer = (msg: string) => {
    if (!msg) return msg;
    return msg.replace(/discount bands/gi, `${productTerm}s`).replace(/discount band/gi, productTerm);
  };

  const userAuthData = useTypedSelector(state => state.userAuthData);
  const [fareProductWarningResolver, setFareProductWarningResolver] = useState(null);
  const intl = useIntl();

  const beforeSubmit = async (fv) => {
    // Zone-aware fare guard. A band carries no route scope, so only the
    // organisation-wide ceiling can be a hard block here; exceeding the
    // cheapest chargeable fare is a warning, and becomes a hard rejection on
    // the assignment screen once actual routes are picked.
    if (fv.discount_mode === 'amount' && fv.organisations_id && fv.valid_date_from) {
      const guardOk = await passesFareGuard({
        scope: 'band',
        discountAmount: fv.discount_amount,
        intl,
        body: {
          organisations_id: fv.organisations_id,
          valid_date_from: fv.valid_date_from,
          valid_time_from: fv.valid_time_from || null,
        },
      });
      if (!guardOk) return { proceed: false };
    }

    if (isPromotionsRoute) return Promise.resolve({ proceed: true });
    const isEmpty = (v) => v === '' || v === null || v === undefined;
    const allFareFieldsEmpty =
      isEmpty(fv.purchase_price) &&
      isEmpty(fv.contract_duration_days) &&
      isEmpty(fv.max_amount) &&
      isEmpty(fv.max_trips);
    if (!allFareFieldsEmpty) return Promise.resolve({ proceed: true });
    return new Promise((resolve) => {
      setFareProductWarningResolver(() => (confirmed) => {
        setFareProductWarningResolver(null);
        if (confirmed) {
          resolve({
            proceed: true,
            updatedValues: { ...fv, requires_documents: false, document_types_ids: [], is_fare_product: false },
            backUrl: '/fare-products/promotions',
          });
        } else {
          resolve({ proceed: false });
        }
      });
    });
  };

  const inputs = React.useMemo(() => [
    { name: 'code', labelKey: 'Code', type: 'text', required: true },
    { name: 'name', labelKey: 'ArName', type: 'text', required: true },
    { name: 'en_name', labelKey: 'EnName', type: 'text', required: true },

    // Dropdowns moved here under Name
    {
      name: 'organisations_id',
      labelKey: 'Organisation',
      type: 'dropdown',
      required: true,
      apiUrl: `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/fareMedia/get_organisations`,
      apiMethod: 'GET',
      dataPath: 'data.data',
      optionConfig: {
        valueKey: 'id',
        labelKey: 'en_name',
        labelKeyAr: 'ar_name'
      }
    },

    {
      name: 'discount_mode',
      labelKey: 'DiscountCalculation',
      type: 'radioGroup',
      defaultValue: 'pct',
      options: [
        { value: 'pct', labelKey: 'Percentage' },
        { value: 'amount', labelKey: 'Amount' }
      ]
    },
    {
      name: 'discount_pct',
      labelKey: 'Percentage',
      type: 'number',
      required: true,
      visibleIf: (values) => values.discount_mode === 'pct'
    },
    {
      name: 'discount_amount',
      labelKey: 'Amount',
      type: 'number',
      required: true,
      visibleIf: (values) => values.discount_mode === 'amount'
    },


    { type: 'divider', labelKey: 'FareProductOptions', hidden: isPromotionsRoute },
    {
      name: 'purchase_price',
      labelKey: 'PurchasePrice',
      type: 'number',
      required: false,
      hidden: isPromotionsRoute,
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if (!hasPP && (hasDur || hasMax || hasMaxTrips)) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },
    {
      name: 'contract_duration_days',
      labelKey: 'ContractDurationDays',
      type: 'number',
      required: false,
      hidden: isPromotionsRoute,
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if (!hasDur && (hasPP || hasMax || hasMaxTrips)) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },
    {
      name: 'max_amount',
      labelKey: 'MaxAmount',
      type: 'number',
      required: false,
      hidden: isPromotionsRoute,
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if (!hasMax && !hasMaxTrips && (hasPP || hasDur)) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },
    {
      name: 'max_trips',
      labelKey: 'MaxTrips',
      type: 'number',
      required: false,
      hidden: isPromotionsRoute,
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if (!hasMax && !hasMaxTrips && (hasPP || hasDur)) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },
    {
      name: 'activation_trigger',
      labelKey: 'ActivationTrigger',
      type: 'radioGroup',
      defaultValue: null,
      hidden: isPromotionsRoute,
      options: [
        { value: 'FIRST_USE', labelKey: 'FirstUse' },
        { value: 'SPECIFIC_DATE', labelKey: 'SpecificDate' }
      ],
      warnMessage: (values) => {
        const hasPP = values.purchase_price !== '' && values.purchase_price !== null && values.purchase_price !== undefined;
        const hasDur = values.contract_duration_days !== '' && values.contract_duration_days !== null && values.contract_duration_days !== undefined;
        const hasMax = values.max_amount !== '' && values.max_amount !== null && values.max_amount !== undefined;
        const hasMaxTrips = values.max_trips !== '' && values.max_trips !== null && values.max_trips !== undefined;
        if ((hasPP || hasDur || hasMax || hasMaxTrips) && !values.activation_trigger) return 'FareProductFieldsIncompleteWarn';
        return null;
      }
    },

    { type: 'divider', labelKey: 'Validity' },
    { name: 'valid_date_from', labelKey: 'ValidFrom', type: 'date', required: true, defaultValue: new Date().toISOString().split('T')[0], style: { width: '270px' } },
    {
      name: 'valid_date_to', 
      labelKey: 'ValidTo', 
      type: 'date',
      defaultValue: '',
      disabled: (values) => values.is_no_expiry === undefined ? true : !!values.is_no_expiry,
      style: { width: '270px' },
      renderExtra: (value, options, intl, setFormValues, formValues) => (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={formValues.is_no_expiry === undefined ? true : !!formValues.is_no_expiry}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormValues(prev => ({
                  ...prev,
                  is_no_expiry: checked,
                  valid_date_to: checked ? '' : (prev.valid_date_to || '')
                }));
              }}
              sx={{ color: '#FF6B38', '&.Mui-checked': { color: '#FF6B38' } }}
            />
          }
          label={intl?.formatMessage({ id: 'NoExpiry' }) || "No Expiry"}
          sx={{ whiteSpace: 'nowrap' }}
        />
      )
    },
    {
      name: 'is_no_expiry',
      type: 'hidden',
      defaultValue: true
    },
    {
      name: 'valid_time_from',
      labelKey: 'TimeFrom',
      type: 'time',
      defaultValue: '00:00',
      disabled: (values) => values.is_all_day === undefined ? true : !!values.is_all_day,
      style: { width: '270px' },
      renderExtra: (value, options, intl, setFormValues, formValues) => (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={formValues.is_all_day === undefined ? true : !!formValues.is_all_day}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormValues(prev => ({
                  ...prev,
                  is_all_day: checked,
                  valid_time_from: checked ? '00:00' : (prev.valid_time_from || ''),
                  valid_time_to: checked ? '23:59' : (prev.valid_time_to || '')
                }));
              }}
              sx={{ color: '#FF6B38', '&.Mui-checked': { color: '#FF6B38' } }}
            />
          }
          label={intl?.formatMessage({ id: 'AllDay' }) || "All Day"}
          sx={{ whiteSpace: 'nowrap' }}
        />
      )
    },
    {
      name: 'is_all_day',
      type: 'hidden',
      defaultValue: true
    },
    {
      name: 'valid_time_to',
      labelKey: 'TimeTo',
      type: 'time',
      defaultValue: '23:59',
      disabled: (values) => values.is_all_day === undefined ? true : !!values.is_all_day,
      style: { width: '270px' }
    },

    {
      name: 'days_of_week_group',
      type: 'checkboxGroup',
      renderExtra: (value, options, intl, setFormValues, formValues) => {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const isAllSelected = days.every(day => !!formValues[day]);
        return (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={isAllSelected}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormValues(prev => {
                    const next = { ...prev };
                    days.forEach(day => next[day] = checked);
                    return next;
                  });
                }}
                sx={{ color: '#FF6B38', '&.Mui-checked': { color: '#FF6B38' } }}
              />
            }
            label={intl?.formatMessage({ id: 'AllDays' }) || "All Days"}
            sx={{ my: 0 }}
          />
        );
      },
      options: [
        { name: 'sunday', labelKey: 'sunday' },
        { name: 'monday', labelKey: 'monday' },
        { name: 'tuesday', labelKey: 'tuesday' },
        { name: 'wednesday', labelKey: 'wednesday' },
        { name: 'thursday', labelKey: 'thursday' },
        { name: 'friday', labelKey: 'friday' },
        { name: 'saturday', labelKey: 'saturday' }
      ]
    },
    { name: 'requires_documents', labelKey: 'RequiresDocuments', type: 'checkbox', defaultValue: false, hidden: isPromotionsRoute },
    {
      name: 'document_types_ids',
      type: 'custom',
      defaultValue: [],
      hidden: isPromotionsRoute,
      visibleIf: (values) => !!values.requires_documents,
      render: (formValues, setFormValues, intl, dropdownOptions) => (
        <div style={{width: '100%', padding: '16px 0', gridColumn: '1 / -1'}}>
          <DocumentTypeTransferList 
              selectedIds={formValues.document_types_ids || []}
              onChange={(ids) => setFormValues(prev => ({ ...prev, document_types_ids: ids }))}
          />
        </div>
      )
    }
  ].filter(input => !input.hidden), [isPromotionsRoute, intl]);

  return (
    <>
    <AddEntity
      pageTitle={pageTitle}
      subsystemName={productType}
      permissionSystem="fares"
      backUrl={backUrl}
      submitApiUrl={`${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/web_portal/fare_media_policy_capping/discount_band`}
      itemsPerColumn={10}
      inputs={inputs}

      dropdowns={[]}

      buildSubmitPayload={(values) => {
        const payload = { ...values };

        // Strictly clean both values first
        payload.discount_pct = null;
        payload.discount_amount = null;

        if (values.discount_mode === 'pct') {
          const val = values.discount_pct;
          payload.discount_pct = (val !== null && val !== "" && val !== undefined) ? parseFloat(val) : null;
        } else if (values.discount_mode === 'amount') {
          const val = values.discount_amount;
          payload.discount_amount = (val !== null && val !== "" && val !== undefined) ? parseFloat(val) : null;
        }

        // Handle empty times and dates
        if (!payload.valid_time_from) payload.valid_time_from = null;
        if (!payload.valid_time_to) payload.valid_time_to = null;
        if (!payload.valid_date_to) payload.valid_date_to = null;

        delete payload.discount_mode;
        delete payload.days_of_week_group;
        delete payload.is_all_day;
        delete payload.is_no_expiry;

        if (payload.purchase_price === '') payload.purchase_price = null;
        if (payload.contract_duration_days === '') payload.contract_duration_days = null;
        if (payload.max_amount === '') payload.max_amount = null;
        if (payload.max_trips === '') payload.max_trips = null;
        // Keep legacy alias in sync for backward compat
        payload.validity_days_after_activation = payload.contract_duration_days || null;
        if (!payload.contract_duration_days) {
          payload.activation_trigger = null;
          payload.max_trips = null; // max_trips only applies to fare products
        }

        // DB constraint chk_fare_product_fields: if duration is set, trigger must also be set
        if (payload.contract_duration_days && !payload.activation_trigger) {
          throw new Error(intl.formatMessage({ id: 'ActivationTriggerRequired' }));
        }

        payload.is_fare_product = !!(
          payload.contract_duration_days || payload.activation_trigger ||
          payload.purchase_price || payload.max_amount || payload.max_trips
        );

        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        days.forEach(day => {
          if (payload[day] === undefined) payload[day] = true;
        });

        // Use the explicit checkbox value, not derived from array length.
        // When requires_documents is false, send empty array so backend deletes associations.
        const requiresDocs = !!payload.requires_documents;
        payload.requires_documents = requiresDocs;
        if (!requiresDocs) {
          payload.document_types_ids = [];
        }

        // Validate: if requires_documents is true, user must select at least one doc type
        if (requiresDocs && (!payload.document_types_ids || payload.document_types_ids.length === 0)) {
          throw new Error(intl.formatMessage({ id: 'RequiresDocumentsValidation' }));
        }

        return payload;
      }}
      successMessage={isPromotionsRoute ? 'PromotionCreatedSuccessfully' : 'FareProductCreatedSuccessfully'}
      messageTransformer={messageTransformer}
      beforeSubmit={beforeSubmit}
      renderAboveSubmit={(formValues, setFormValues, intl) => (
        <ApplicableFaresPanel
          scope="band"
          intl={intl}
          discountAmount={formValues.discount_mode === 'amount' ? formValues.discount_amount : null}
          missingKey="PleaseSelectOrgAndDate"
          hintKey="DiscountBandFaresHint"
          buildBody={() => {
            if (!formValues.organisations_id || !formValues.valid_date_from) return null;
            return {
              organisations_id: formValues.organisations_id,
              valid_date_from: formValues.valid_date_from,
              valid_time_from: formValues.valid_time_from || null,
            };
          }}
        />
      )}
    />
    <Modal
      open={!!fareProductWarningResolver}
      title={intl.formatMessage({ id: 'FareProductEmptyFieldsWarningTitle' })}
      closable={false}
      footer={null}
    >
      <p style={{ marginBottom: 20 }}>
        {intl.formatMessage({ id: 'FareProductEmptyFieldsWarningMsg' })}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => fareProductWarningResolver(false)}
          className="updateBtnStyle"
          style={{ backgroundColor: '#6c757d' }}
        >
          {intl.formatMessage({ id: 'GoBackFillFields' })}
        </button>
        <button
          type="button"
          onClick={() => fareProductWarningResolver(true)}
          className="updateBtnStyle"
          style={{ backgroundColor: '#ff4d4f' }}
        >
          {intl.formatMessage({ id: 'ContinueAsPromotion' })}
        </button>
      </div>
    </Modal>
    </>
  );
};

export default AddDiscountBand;
